/* src/sections/auth/festival/themes/OnamTheme.js */
import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------
// Colors extracted from the new reference image (Sun, Boat, Waves)
const COLORS = {
    // Sky
    SKY_TOP: '#FF9800',       // Orange
    SKY_MID: '#FFB74D',       // Light Orange
    SKY_BOTTOM: '#FFF3E0',    // Pale

    // Sun
    SUN_CORE: '#FFFF00',      // Bright Yellow
    SUN_GLOW: '#FFF176',      // Light Yellow

    // Water (Stylized Waves)
    WAVE_BACK: '#81D4FA',     // Light Blue
    WAVE_MID: '#29B6F6',      // Blue
    WAVE_FRONT: '#E1F5FE',    // White/Foam

    // Boat
    BOAT_BODY: '#5D4037',     // Nature
    BOAT_TAIL: '#8D6E63',     // Light Brown
    FLAG_ORANGE: '#FF6F00',   // Saffron flag
    LEAF_DARK: '#1B5E20',     // Deep Green
    LEAF_MID: '#2E7D32',      // Medium Green
    LEAF_LIGHT: '#43A047',    // Light Green
    LEAF_SHADOW: '#003300',   // Shadow

    // Rowers
    SKIN_DARK: '#3E2723',
    DRESS_WHITE: '#FFFFFF',
};

// ----------------------------------------------------------------------

function OnamCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let time = 0;

        let width = 0;
        let height = 0;

        const resizeCanvas = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
        };
        resizeCanvas();

        // 1. Draw Sky Gradient
        const drawSky = () => {
            const gradient = ctx.createLinearGradient(0, 0, 0, height);
            gradient.addColorStop(0, COLORS.SKY_TOP);
            gradient.addColorStop(0.4, COLORS.SKY_MID);
            gradient.addColorStop(1, COLORS.SKY_BOTTOM);
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, width, height);
        };

        // 2. Draw Big Sun
        const drawSun = () => {
            const centerX = width / 2;
            const centerY = height * 0.35; // Positioned behind text
            const radius = Math.min(width, height) * 0.35; // Large sun

            ctx.save();
            ctx.shadowBlur = 50;
            ctx.shadowColor = COLORS.SUN_GLOW;

            const gradient = ctx.createRadialGradient(centerX, centerY, radius * 0.2, centerX, centerY, radius);
            gradient.addColorStop(0, COLORS.SUN_CORE);
            gradient.addColorStop(1, COLORS.SUN_GLOW);

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            return { centerX, centerY, radius };
        };

        // 3. Draw Birds
        const drawBirds = () => {
            const birdCount = 5;
            ctx.fillStyle = '#000';
            ctx.beginPath();
            for (let i = 0; i < birdCount; i += 1) {
                const bx = (width * 0.2) + (i * 60) + Math.sin(time * 0.01 + i) * 20;
                const by = (height * 0.2) + (i % 2 === 0 ? -10 : 10) + Math.sin(time * 0.02) * 5;

                // Simple V shape
                ctx.moveTo(bx, by);
                ctx.quadraticCurveTo(bx + 10, by + 5, bx + 20, by - 5); // Right wing tip
                ctx.quadraticCurveTo(bx + 10, by, bx, by + 5); // Center
                ctx.quadraticCurveTo(bx - 10, by, bx - 20, by - 5); // Left wing tip
                ctx.quadraticCurveTo(bx - 10, by + 5, bx, by); // Back to center
            }
            ctx.fill();
        };

        // 6. Draw Dense Top Hanging Leaves (New Update)
        const drawNeedleLeaf = (x, y, length, angle, color) => {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(angle);

            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.quadraticCurveTo(length / 2, length / 4, 0, length);
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.stroke();

            // Needles/Fronds
            for (let i = 0; i < length; i += 3) {
                const spread = (length - i) * 0.5;

                ctx.beginPath();
                ctx.moveTo(0, i);
                ctx.lineTo(-spread, i + 15);
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(0, i);
                ctx.lineTo(spread, i + 15);
                ctx.stroke();
            }
            ctx.restore();
        };

        const drawTopDecoration = () => {
            const leafCount = Math.floor(width / 30);

            // Layer 1 (Back/Dark)
            for (let i = 0; i <= leafCount; i += 1) {
                const x = i * 30;
                const len = 100 + Math.sin(i * 0.5 + time * 0.02) * 20;
                drawNeedleLeaf(x, -10, len, Math.sin(time * 0.01 + i) * 0.2, COLORS.LEAF_SHADOW);
            }

            // Layer 2 (Mid)
            for (let i = 0; i <= leafCount; i += 1) {
                const x = (i * 30) + 15;
                const len = 120 + Math.sin(i * 0.8 + time * 0.03) * 30;
                drawNeedleLeaf(x, -10, len, Math.sin(time * 0.02 + i) * 0.3, COLORS.LEAF_DARK);
            }

            // Layer 3 (Front/Light)
            for (let i = 0; i <= leafCount; i += 2) {
                const x = (i * 30) + 10;
                const len = 140 + Math.sin(i * 0.3 + time * 0.02 + 1) * 20;
                drawNeedleLeaf(x, -20, len, Math.sin(time * 0.01 + i + 1) * 0.4, COLORS.LEAF_MID);
            }
        };

        // 4. Draw Stylized Waves
        const drawWaveLayer = (yOffset, color, speed, amplitude) => {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(0, height);

            for (let x = 0; x <= width; x += 10) {
                const y = (height * 0.75) + yOffset + Math.sin(x * 0.005 + time * speed) * amplitude;
                ctx.lineTo(x, y);
            }

            ctx.lineTo(width, height);
            ctx.lineTo(0, height);
            ctx.fill();
        };

        // 5. Draw Stylized Boat (Curved Tail)
        const drawBoat = () => {
            const boatScale = width < 900 ? 0.6 : 0.9;
            const boatY = height * 0.75 + Math.sin(time * 0.03) * 10;
            const boatX = width / 2;

            ctx.save();
            ctx.translate(boatX, boatY);
            ctx.scale(boatScale, boatScale);

            // Boat Body (Curved Swoosh)
            ctx.beginPath();
            // Start left/front
            ctx.moveTo(-300, 20);
            // Bottom curve to right/tail
            ctx.bezierCurveTo(-100, 100, 200, 100, 350, -150); // High tail
            // Top curve back
            ctx.bezierCurveTo(250, 0, 100, 40, -280, 0);
            // Tip
            ctx.quadraticCurveTo(-310, -5, -300, 20);

            const boatGrad = ctx.createLinearGradient(-300, 0, 300, 0);
            boatGrad.addColorStop(0, COLORS.BOAT_BODY);
            boatGrad.addColorStop(1, COLORS.BOAT_TAIL);
            ctx.fillStyle = boatGrad;
            ctx.fill();

            // Flag on Tail
            ctx.beginPath();
            ctx.moveTo(340, -130);
            ctx.quadraticCurveTo(380, -140, 360, -100);
            ctx.quadraticCurveTo(340, -110, 340, -130);
            ctx.fillStyle = COLORS.FLAG_ORANGE;
            ctx.fill();

            // Rowers (Stylized Silhouettes)
            const rowerStart = -200;
            const rowerEnd = 150;
            const rowerCount = 12;
            const step = (rowerEnd - rowerStart) / rowerCount;

            for (let i = 0; i < rowerCount; i += 1) {
                const rx = rowerStart + i * step;
                // Follow boat curve roughly
                const ry = 40 + (i * i * -0.2); // slight arch

                ctx.save();
                ctx.translate(rx, ry);

                // Body
                ctx.fillStyle = COLORS.DRESS_WHITE;
                ctx.beginPath();
                ctx.moveTo(-10, 0);
                ctx.lineTo(10, 0);
                ctx.lineTo(5, -25);
                ctx.lineTo(-5, -25);
                ctx.fill();

                // Head
                ctx.fillStyle = COLORS.SKIN_DARK;
                ctx.beginPath();
                ctx.arc(0, -30, 6, 0, Math.PI * 2);
                ctx.fill();

                // Oar
                const rowPhase = Math.sin(time * 0.1 + i * 0.3);
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(0, -15);
                ctx.lineTo(rowPhase * 20, 30);
                ctx.stroke();

                ctx.restore();
            }

            ctx.restore();
        };

        const animate = () => {
            time += 1;
            ctx.clearRect(0, 0, width, height);

            drawSky();
            const sun = drawSun();
            drawBirds();

            // Draw Waves (Back layers)
            drawWaveLayer(0, COLORS.WAVE_BACK, 0.02, 20);
            drawWaveLayer(20, COLORS.WAVE_MID, 0.03, 15);

            // Draw Boat
            drawBoat();

            // Draw Waves (Front layer/Foam)
            drawWaveLayer(40, alpha(COLORS.WAVE_FRONT, 0.8), 0.04, 10);

            // Draw Top Decoration (Leaves)
            drawTopDecoration();

            // Draw Text Inside Sun
            if (sun) {
                ctx.save();
                ctx.translate(sun.centerX, sun.centerY);
                ctx.fillStyle = '#8E24AA'; // Deep Purple/Reddish for contrast
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                const fontSize = sun.radius * 0.2;

                // "HAPPY"
                ctx.font = `600 ${fontSize}px sans-serif`;
                ctx.fillText('HAPPY', 0, -fontSize * 0.6);

                // "ONAM"
                ctx.font = `700 ${fontSize * 1.8}px serif`; // Artistic font
                ctx.fillText('Onam', 0, fontSize * 0.6);

                ctx.restore();
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        const handleResize = () => {
            resizeCanvas();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
            }}
        />
    );
}

// ----------------------------------------------------------------------

export default function OnamTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            overflow: 'hidden',
            bgcolor: COLORS.SKY_BOTTOM,
        }}>
            <OnamCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 2, position: 'relative',
                bgcolor: isDark ? 'rgba(0,10,5,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.LEAF_MID, 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.4)}`,
                    `0 16px 64px ${alpha(COLORS.LEAF_MID, 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${COLORS.LEAF_MID}, ${COLORS.SUN_CORE}, ${COLORS.LEAF_MID}, transparent)`,
                    borderRadius: '0 0 4px 4px',
                    animation: 'onamGlow 3s ease-in-out infinite',
                },
                '@keyframes onamGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.LEAF_DARK}, ${COLORS.SUN_CORE}, ${COLORS.LEAF_MID}, ${COLORS.LEAF_DARK})`,
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.LEAF_MID, 0.5)}`,
                    }}>🌸 &nbsp; Happy Onam &nbsp; 🌸</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2 }}><Logo /></Stack>
                {cloneElement(children, { hideHelpLink: true })}
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.LEAF_MID, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

OnamTheme.propTypes = {
    children: PropTypes.node,
};
